import * as React from "react";
import { type ReactNode } from "react";
import {
  TreeItemCheckbox,
  TreeItemContent,
  TreeItemGroupTransition,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemProvider,
  TreeItemRoot,
  useTreeItem,
  type UseTreeItemParameters,
} from "@mui/x-tree-view-pro";
import { styled } from "~/common/mui";
import { ExpandedIcon } from "./ExpandedIcon";

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface BaseTreeItemProps
  extends Omit<UseTreeItemParameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {
  /** Custom content rendered inside TreeItemLabel. Receives the raw label string. */
  renderContent?: (label: React.ReactNode) => ReactNode;
}

// ────────────────────────────────────────────
// Styled components
// ────────────────────────────────────────────

const StyledTreeItemContent = styled(TreeItemContent)({
  padding: "8px 12px",
  gap: "12px",
});

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

export const BaseTreeItem = React.forwardRef(function BaseTreeItem(
  props: BaseTreeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const { id, itemId, label, disabled, children, renderContent, ...other } = props;

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

  return (
    <TreeItemProvider id={id} itemId={itemId}>
      <TreeItemRoot {...getRootProps(other)}>
        <StyledTreeItemContent {...getContentProps()}>
          <TreeItemCheckbox {...getCheckboxProps()} />
          <TreeItemLabel {...getLabelProps()}>{renderContent ? renderContent(label) : label}</TreeItemLabel>
          {status.expandable && (
            <TreeItemIconContainer {...getIconContainerProps()}>
              <ExpandedIcon expanded={status.expanded} />
            </TreeItemIconContainer>
          )}
        </StyledTreeItemContent>
        {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
      </TreeItemRoot>
    </TreeItemProvider>
  );
});
